import React, { useContext, useEffect, useState } from "react";
import { Button, Form, Modal, OverlayTrigger } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { LS_AUTHORITY_KEY, USER_ROLE } from "../../../core/constants/common";
import GeneralDisclosureService from "../../../core/service/GeneralDisclosureService";
import { BiPlusCircle, BiTransfer, BiTrash } from "react-icons/bi";
import { AiOutlineDelete } from "react-icons/ai";
import LanguageSelectorService from "../../../core/service/LanguageSelectorService";
import { LoaderContext } from "../../../App";
import Utils from "../../../core/utilities/Utils";
import QuestionService from "../../../core/service/QuestionService";
import { toast } from "react-toastify";

const GDQuestionFormPopup = ({ t, saveCallback, disclosureId, question }) => {

  const authority = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
  const [show, setShow] = useState(true);
  const [validated, setValidated] = useState(false);
  const localizationContent = {
    fieldName: "",
    languageCode: '',
    question: "",
    help_text: "",
    code: ""
  }
  const [formData, setFormData] = useState(
    question.id > 0
      ? question
      : { children: [], localizationContent: [localizationContent], singleAnswer: true }
  );
  const [primaryLanguageCode, setPrimaryLanguageCode] = useState();
  const [languageOptions, setLanguageOptions] = useState([]);
  const { setLoaderDisplay } = useContext(LoaderContext);
  useEffect(() => {
    loadLanguages();
  }, []);

  useEffect(() => {
    setFormData(prevFormData => {
      const updatedFormData = { ...prevFormData };
      if (updatedFormData.localizationContent && updatedFormData.localizationContent[0]) {
        // Update the languageCode of the first object
        updatedFormData.localizationContent[0] = {
          ...updatedFormData.localizationContent[0],
          languageCode: primaryLanguageCode
        };
      }

      // If there are children, update the first localizationContent inside them
      if (updatedFormData.children) {
        updatedFormData.children = updatedFormData.children.map(child => {
          if (child.localizationContent && child.localizationContent[0]) {
            child.localizationContent[0] = {
              ...child.localizationContent[0],
              languageCode: primaryLanguageCode
            };
          }
          return child;
        });
      }

      return updatedFormData;
    });
  }, [primaryLanguageCode]);


  const loadLanguages = () => {
    LanguageSelectorService.getLanguages().then(res => {
      setLanguageOptions(res.data);
      const primaryCode = res.data.find(language => language.primary)?.code;
      setPrimaryLanguageCode(primaryCode);
      const updatedFormData = { ...formData };
      if (question.id > 0) {
        if (updatedFormData.localizedContents.length > 0) {
          // Update the outer layer (parent) localizationContent
          updatedFormData['localizationContent'] = updateLocalizationContentRecursively(
            updatedFormData.localizedContents,
            primaryCode
          );
        } else {
          updatedFormData['localizationContent'] = [localizationContent];
          updatedFormData.localizationContent[0] = {
            ...updatedFormData.localizationContent[0],
            question: updatedFormData.name,
            help_text: updatedFormData.helpText,
            languageCode: primaryCode
          };
        }

        // Update the children recursively (inside children array)
        if (updatedFormData.children && updatedFormData.children.length > 0) {
          updatedFormData.children = updatedFormData.children.map((child) => {
            if (child.localizedContents && child.localizedContents.length > 0) {
              // If localizedContents has values, call updateLocalizationContentRecursively
              child['localizationContent'] = updateLocalizationContentRecursively(
                child.localizedContents,
                primaryCode
              );
            } else {
              // Otherwise, set localizationContent to an array with localizationContent
              child['localizationContent'] = [localizationContent];
              child.localizationContent[0] = {
                ...child.localizationContent[0],
                question: child.name,
                help_text: child.helpText,
                languageCode: primaryCode
              };
            }
            return child;
          });
        }
        setFormData(updatedFormData);
      } else {

      }

    }).catch(error => {
      setLoaderDisplay(false);
    });
  };

  const updateLocalizationContentRecursively = (localizationContent, primaryCode) => {
    // Group and transform localizationContent for each content item
    const groupedByLanguage = groupByLanguage(localizationContent);
    const customArray = transformGroupedData(groupedByLanguage);
    const mappedData = mapToStateFormat(customArray, primaryCode);
    return mappedData;
  };

  // Function to group items by language
  const groupByLanguage = (items) => {
    return items.reduce((acc, item) => {
      const { languageCode } = item;
      if (!acc[languageCode]) acc[languageCode] = [];
      acc[languageCode].push(item);
      return acc;
    }, {});
  };


  const transformGroupedData = (groupedData) => {
    return Object.keys(groupedData).map((language) => {
      const translations = groupedData[language].reduce((acc, item) => {
        acc[item.fieldName] = item.translatedValue;
        return acc;
      }, {});

      return {
        language,
        translations,
      };
    });
  };

  const mapToStateFormat = (customArray, primaryCode) => {
    const result = customArray
      .map((item) => ({
        fieldName: "",
        languageCode: item.language,
        question: item.translations.question,
        help_text: item.translations.help_text
      }));

    // Check if there is no object with primaryCode
    if (!result.some(item => item.languageCode === primaryCode) && result.length > 0) {
      result[0].languageCode = primaryCode;
    }

    // Sort result
    const sortedResult = result.sort((a, b) => {
      if (a.languageCode === primaryCode) return -1;
      if (b.languageCode === primaryCode) return 1;
      return 0;
    });

    return sortedResult;

  };


  const handleClose = () => {
    setShow(false);
    saveCallback(false);
  };

  const handleSave = () => {
    const transformedData = transformLocalizationContent({ ...formData });
    if (question.id > 0) {
      transformedData.id = question.id;
    };
    // return
    GeneralDisclosureService.addOrUpdateQuestion(disclosureId, transformedData, authority.roles.includes(USER_ROLE.SUPER_ADMIN))
      .then((response) => {
        setShow(false);
        if (response.status !== 201 && response.status !== 200) return;

        saveCallback(true);
      }).catch((error) => {
        setShow(false);
        saveCallback(false);
      });
    setValidated(true);
  };
  const transformLocalizationContent = (data) => {
    const transformLocalizedContent = (localizationContent) => {
      return localizationContent.flatMap((item) => [
        {
          fieldName: 'question',
          languageCode: item.languageCode,
          translatedValue: item.question
        },
        {
          fieldName: 'help_text',
          languageCode: item.languageCode,
          translatedValue: item.help_text ?? ''
        }
      ]);
    };

    const getLocalizedValue = (localizationContent, fieldName) => {
      const item = localizationContent.find(item => item.languageCode == primaryLanguageCode);
      return item ? item[fieldName] : null;
    };
    const updatedName = getLocalizedValue(data.localizationContent, 'question');
    const updatedHelpText = getLocalizedValue(data.localizationContent, 'help_text');
    const transformedData = {
      ...data,
      name: updatedName || data.name,
      helpText: updatedHelpText || data.helpText,
      localizedContents: transformLocalizedContent(data.localizationContent),
      children: data.children.map((child) => {
        const updatedChildName = getLocalizedValue(child.localizationContent, 'question');
        const updatedChildHelpText = getLocalizedValue(child.localizationContent, 'help_text');

        // Transform each child object similarly
        const transformedChild = {
          ...child,
          name: updatedChildName || child.name,
          helpText: updatedChildHelpText || child.helpText,
          localizedContents: transformLocalizedContent(child.localizationContent)
        };
        delete transformedChild.localizationContent;
        return transformedChild;
      })
    };
    delete transformedData.localizationContent;
    return transformedData;
  };


  const handleLocalizationChange = (qIndex, index, field, value) => {
    const updatedFormData = { ...formData };
    if (qIndex < 0) {
      updatedFormData.localizationContent[index] = {
        ...updatedFormData.localizationContent[index],
        [field]: value
      };
    } else {
      updatedFormData.children[qIndex].localizationContent[index] = {
        ...updatedFormData.children[qIndex].localizationContent[index],
        [field]: value
      };
    }

    setFormData(updatedFormData);
  };


  const getAvailableOptions = (code, qIndex) => {
    let dataChoosed = [];
    if (qIndex >= 0) {
      dataChoosed = formData.children[qIndex].localizationContent;
    } else {
      dataChoosed = formData.localizationContent;
    }
    const selectedValues = dataChoosed.map(row => row.languageCode).filter(Boolean);

    const filtered = languageOptions.filter(
      option => {
        return !selectedValues.includes(option.code) ||
          dataChoosed.find(row => row.languageCode === code)?.languageCode === option.code
      }

    );

    return filtered;
  };


  const addLocalizationRow = (qIndex) => {
    if (checkIsQuestion(qIndex)) {
      setFormData((prevData) => ({
        ...prevData,
        localizationContent: [...prevData.localizationContent, localizationContent],
      }));
    } else {
      // If it's not a question, add to the correct index in children
      setFormData((prevData) => {
        const updatedChildren = [...prevData.children];

        updatedChildren[qIndex] = {
          ...updatedChildren[qIndex],
          localizationContent: [...updatedChildren[qIndex].localizationContent, localizationContent],
        };

        return {
          ...prevData,
          children: updatedChildren,
        };
      });
    }
  };

  const checkIsQuestion = (qIndex) => {
    let isQuestion = true;
    if (qIndex >= 0) {
      isQuestion = false;
    }
    return isQuestion;
  }

  const handleTranslate = (targetLang, index, qIndex) => {
    var mainObj = {};
    const isQuestion = checkIsQuestion(qIndex);
    if (isQuestion) {
      mainObj = formData.localizationContent;
    } else {
      mainObj = formData.children[qIndex].localizationContent;
    };
    const sourceLang = mainObj[0].languageCode;
    const textArray = [];
    if (mainObj[0].question && mainObj[0].question != "") {
      textArray.push(mainObj[0].question);
    } else {
      toast.warning(t("ProvideTextMsg.label"), {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    };
    if (mainObj[0].question && mainObj[0].question != "") {
      textArray.push(mainObj[0].help_text);
    };
    if (textArray.length > 0
      && sourceLang != null && sourceLang != ''
      && targetLang != null && targetLang != '') {
      QuestionService.translateText(textArray, sourceLang, targetLang).then(res => {
        if (res.status !== 200) return;
        const result = res.data.translations;
        const updatedFormData = { ...formData };

        if (isQuestion) {
          const updatedLocalizationContent = [...updatedFormData.localizationContent];
          updatedLocalizationContent[index] = {
            ...updatedLocalizationContent[index],
            question: result[0]?.text,
            code: mainObj[0].code,
            ...(result[1]?.text && { help_text: result[1]?.text })
          };

          setFormData({
            ...updatedFormData,
            localizationContent: updatedLocalizationContent
          });
        } else {
          const updatedChildren = [...updatedFormData.children];
          const updatedLocalizationContent = [...updatedChildren[qIndex].localizationContent];

          updatedLocalizationContent[index] = {
            ...updatedLocalizationContent[index],
            question: result[0]?.text,
            code: mainObj[0].code,
            ...(result[1]?.text && { help_text: result[1]?.text })
          };

          updatedChildren[qIndex] = {
            ...updatedChildren[qIndex],
            localizationContent: updatedLocalizationContent
          };

          setFormData({
            ...updatedFormData,
            children: updatedChildren
          });
        }

      }).catch(err => { });
    } else {
      let message = '';
      if (sourceLang == '') {
        message = t("ProvideSourceMsg.label");
      } else if (targetLang == '') {
        message = t("ProvideTargetMsg.label");
      } else if (textArray.length > 0) {
        message = t("ProvideTextMsg.label");
      }
      toast.warning(message, {
        position: toast.POSITION.TOP_RIGHT,
      });
    }

  };

  const deleteLocalization = (index, qIndex) => {
    const updatedData = { ...formData };
    if (qIndex === -1) {
      updatedData.localizationContent.splice(index, 1); // Remove localization from the outer question
    } else {
      updatedData.children[qIndex].localizationContent.splice(index, 1); // Remove localization from the child
    }
    setFormData(updatedData);
  };

  const renderQuestionFields = (qIndex = -1) => {
    // const nameValue = qIndex >= 0 ? formData.children[qIndex]?.name : formData?.name;
    const codeValue = qIndex >= 0 ? formData.children[qIndex]?.code : formData?.code;
    // const helpTextValue = qIndex >= 0 ? formData.children[qIndex]?.helpText : formData?.helpText;
    const singleAnswerValue = qIndex >= 0 ? formData.children[qIndex]?.singleAnswer : formData?.singleAnswer;
    const iteratelocalizationContentArray = qIndex >= 0 ? formData.children[qIndex]?.localizationContent : formData?.localizationContent;
    return (
      <>{iteratelocalizationContentArray
        && iteratelocalizationContentArray.length > 0
        && iteratelocalizationContentArray.map((item, index) =>
        (<div id={`questionContainer-${index + 1}`} key={`questionContainer-${index + 1}`} className="gd-question-03 Single_RC_01">
          <div className="row m-0 align-items-center">
            <div className="col-lg-10 gd-quest-col p-0">
              <div className="row m-0">
                <div className="col-lg-2 p-2">
                  <label className="mb-1">{t("Language.label")} *</label>
                  <select className="form-select" value={item.languageCode}
                    disabled={item.languageCode == primaryLanguageCode}
                    onChange={(e) => handleLocalizationChange(qIndex, index, "languageCode", e.target.value)}>
                    <option value="">{t("SelectLanguage.label")}</option>
                    {getAvailableOptions(item.languageCode, qIndex).map(option => (
                      <option value={option.code} key={option.code}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-lg-10 p-2">
                  <label className="mb-1">{t("Name.label")} *</label>
                  <input
                    type="text"
                    className="form-control"
                    name={`questionContainer-${index + 1}-question`}
                    required
                    autocomplete="off"
                    value={item.question}
                    onChange={(e) => {
                      handleLocalizationChange(qIndex, index, "question", e.target.value)
                      if (qIndex >= 0) {
                        const q = JSON.parse(JSON.stringify(formData));
                        q.children[qIndex].name = e.target.value;
                        setFormData(q);
                      } else {
                        setFormData({ ...formData, name: e.target.value });
                      }
                    }}
                  />
                </div>
                <div className="col-lg-12 p-2">
                  <label className="mb-1">{t("HelpText.label")}</label>
                  <textarea
                    className="form-control"
                    name={`questionContainer-${index + 1}-help`}
                    required
                    autocomplete="off"
                    value={item.help_text}
                    onChange={(e) => {
                      handleLocalizationChange(qIndex, index, "help_text", e.target.value)
                      if (qIndex >= 0) {
                        const q = JSON.parse(JSON.stringify(formData));
                        q.children[qIndex].helpText = e.target.value;
                        setFormData(q);
                      } else {
                        setFormData({ ...formData, helpText: e.target.value });
                      }
                    }}>
                  </textarea>
                </div>
              </div>
            </div>
            <div className="col-lg-2 p-2">
              <ul className='stakeCat_add'>
                {
                  (index > 0) && (
                    <OverlayTrigger
                      placement="top"
                      overlay={Utils.renderTooltip(
                        t("ClickToTranslate.label")
                      )}>
                      <li>
                        <span className='cursor' onClick={() => handleTranslate(item.languageCode, index, qIndex)}>
                          <BiTransfer />
                        </span>
                      </li>
                    </OverlayTrigger>
                  )
                }
                {
                  (iteratelocalizationContentArray.length > 1 && (item.languageCode != primaryLanguageCode)) && (
                    <OverlayTrigger
                      placement="top"
                      overlay={Utils.renderTooltip(
                        t("DeleteTranslation.label")
                      )}>
                      <li>
                        <span className='cursor' onClick={() => deleteLocalization(index, qIndex)}>
                          <BiTrash />
                        </span>
                      </li>
                    </OverlayTrigger>
                  )}
                {
                  ((index == iteratelocalizationContentArray.length - 1)
                    && iteratelocalizationContentArray.length < languageOptions.length) && (
                    <OverlayTrigger
                      placement="top"
                      overlay={Utils.renderTooltip(
                        t("AddTranslation.label")
                      )}>
                      <li>
                        <Button variant='GroupStake' className='btn-outline-primary border-0' onClick={() => addLocalizationRow(qIndex)}> <BiPlusCircle /></Button>
                      </li>
                    </OverlayTrigger>
                  )}
              </ul>
            </div>
          </div>


        </div>))}
        <div className="col-lg-2 p-2">
          <label className="mb-1">{t("Code.label")} *</label>
          <input
            type="text"
            name={`code`}
            className="form-control"
            required
            autocomplete="off"
            value={codeValue}
            onChange={(e) => {
              if (qIndex >= 0) {
                const q = JSON.parse(JSON.stringify(formData));
                q.children[qIndex].code = e.target.value;
                setFormData(q);
              } else {
                setFormData({ ...formData, code: e.target.value });
              }
            }}
          />
        </div>
        <div className="p-2 d-flex align-items-inline">
          <input className="form-check-input" type="checkbox"
            id={`singleAnswerCheckbox-${qIndex}`}
            checked={singleAnswerValue}
            onChange={() => {
              if (qIndex >= 0) {
                const q = JSON.parse(JSON.stringify(formData));
                q.children[qIndex].singleAnswer = !singleAnswerValue;
                setFormData(q);
              } else {
                setFormData({ ...formData, singleAnswer: !singleAnswerValue });
              }
            }} />
          <label className="form-check-label mx-1" htmlFor={`singleAnswerCheckbox-${qIndex}`}>
            {t("SingleAnswer.label")}
          </label>
        </div>
      </>
    );
  };

  const onAddNewSubQuestion = () => {
    const q = JSON.parse(JSON.stringify(formData));
    if (!q.children) {
      q.children = [];
    }
    q.children.push({
      name: '',
      code: '',
      helpText: '',
      singleAnswer: true,
      localizationContent: [{
        fieldName: "",
        languageCode: primaryLanguageCode,
        question: "",
        help_text: "",
        code: ""
      }]
    });
    setFormData(q);
  };


  const onDeleteSubQuestion = (qIndex) => {
    const q = JSON.parse(JSON.stringify(formData));
    if (q.children[qIndex]) {
      q.children.splice(qIndex, 1);
    }
    setFormData(q);
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="xl"
        aria-labelledby="contained-modal-title-vcenter"
        centered>
        <Modal.Header closeButton>
          <Modal.Title as="h5">
            {question.id > 0 ? t("EditQuestion.label") : t("NewQuestion.label")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form noValidate validated={validated} autoComplete="off">
            {renderQuestionFields()}
            {formData.children?.length > 0 && <h5>{t('SubQuestions.label')}</h5>}
            {
              formData.children
                ?.sort((a, b) => a.sortOrder - b.sortOrder)
                ?.map((child, cIndex) => {
                  return (
                    <div className="card mt-4">
                      <div className="card-header">
                        <span className="float-left">{t('SubQuestion.label')} {cIndex + 1}</span>
                        <span className="float-right cursor" onClick={() => onDeleteSubQuestion(cIndex)}><AiOutlineDelete /></span>
                      </div>
                      <div className="card-body">
                        {renderQuestionFields(cIndex)}
                      </div>
                    </div>
                  );
                })
            }
            <a className="text-primary cursor" onClick={() => onAddNewSubQuestion()}>
              <BiPlusCircle className="me-2"></BiPlusCircle>{t('AddSubQuestion.label')}
            </a>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            {t("Cancel.label")}
          </Button>
          <Button variant="primary" onClick={handleSave} className="sbmBtn">
            {question.id > 0 ? t("Update.label") : t("Create.label")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
export default withTranslation()(GDQuestionFormPopup);
